import { Box, Button, Typography, makeStyles } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { FC, useEffect, useMemo, useRef } from "react";
import { ArrayInput, DateTimeInput, SimpleFormIterator } from "react-admin";
import { useForm, useFormState } from "react-final-form";

const MAX_FIELDS = 6;

const useStyles = makeStyles({
  dateIterator: {
    "& > li": {
      marginBottom: 16,
    },
  },
});

interface MultipleSurgeryDatesProps {
  source?: string;
  label?: string;
  isCreate?: boolean;
}

export const MultipleSurgeryDates: FC<MultipleSurgeryDatesProps> = ({
  source = "dates",
  label = "Surgery Date",
  isCreate = false,
}) => {
  const classes = useStyles();
  const form = useForm();
  const formState = useFormState();
  const previousCase1Date = useRef<string>("");

  // Get current array length
  const currentArray = useMemo(
    () => formState.values?.[source] || [],
    [formState, source],
  );
  const currentLength = Array.isArray(currentArray) ? currentArray.length : 0;

  // Check if we can add more fields
  const canAddMore = currentLength < MAX_FIELDS;

  // Auto-populate logic: ONLY when Case 1 date changes, copy to other empty cases
  useEffect(() => {
    if (!isCreate) return; // Only for create mode

    const case1Date = currentArray[0];

    // Only proceed if Case 1 has a value AND it actually changed
    if (
      case1Date &&
      case1Date !== "" &&
      case1Date !== previousCase1Date.current
    ) {
      // Update previousCase1Date
      previousCase1Date.current = case1Date;

      // Extract date part from Case 1
      let datePart = "";
      if (case1Date.includes(" ")) {
        [datePart] = case1Date.split(" ");
      } else if (case1Date.includes("T")) {
        [datePart] = case1Date.split("T");
      }

      if (datePart) {
        // Update other cases that are empty
        const newArray = [...currentArray];
        let hasChanges = false;

        for (let i = 1; i < currentLength; i++) {
          if (!newArray[i] || newArray[i] === "") {
            // Set same date with default time 09:00:00
            newArray[i] = `${datePart} 09:00:00`;
            hasChanges = true;
          }
        }

        if (hasChanges) {
          form.change(source, newArray);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentArray[0], currentLength, isCreate, form, source, currentArray]);

  // Custom parse function that handles the datetime-local input
  const parseDateTime = (value: string) => {
    if (!value) return "";
    // Convert from datetime-local format (YYYY-MM-DDTHH:mm) to SQL format (YYYY-MM-DD HH:mm:ss)
    return value.replace("T", " ") + ":00";
  };

  // Custom format function that handles the SQL datetime
  const formatDateTime = (value: string) => {
    if (!value) return "";

    // Handle SQL format: "YYYY-MM-DD HH:mm:ss"
    if (value.includes(" ")) {
      const [datePart, timePart] = value.split(" ");
      if (datePart && timePart) {
        const [hh, mm] = timePart.split(":");
        return `${datePart}T${hh}:${mm}`;
      }
    }

    // Handle ISO format: "YYYY-MM-DDTHH:mm:ss" or with timezone
    if (value.includes("T")) {
      const [datePart, timePartRaw] = value.split("T");
      if (datePart && timePartRaw) {
        // Remove timezone and seconds
        const timePart = timePartRaw
          .replace(/([+-]\d{2}:?\d{2}|Z)$/, "")
          .split(":")
          .slice(0, 2)
          .join(":");
        return `${datePart}T${timePart}`;
      }
    }

    return "";
  };

  return (
    <Box>
      <Typography variant="h6" style={{ marginTop: 16, marginBottom: -20 }}>
        {label}
      </Typography>
      <ArrayInput source={source} label="" key={`${source}-${currentLength}`}>
        <SimpleFormIterator
          className={classes.dateIterator}
          addButton={
            canAddMore ? (
              <Button
                startIcon={<AddIcon />}
                variant="outlined"
                color="primary"
                size="small"
              >
                Add Case ({currentLength}/{MAX_FIELDS})
              </Button>
            ) : (
              <Typography variant="caption" color="textSecondary">
                Maximum {MAX_FIELDS} cases reached
              </Typography>
            )
          }
          disableReordering
          disableAdd={!canAddMore}
          getItemLabel={(index) => `Case ${index + 1}`}
        >
          <DateTimeInput
            source={isCreate ? "" : "date"}
            label=""
            parse={parseDateTime}
            format={formatDateTime}
            fullWidth
          />
        </SimpleFormIterator>
      </ArrayInput>
    </Box>
  );
};
